import { Link } from '@change/ds-core-ui';
import type { LinkProps } from '@change/ds-core-ui';

import { parseHtml } from '@change-corgi/core-react/html';
import type { ParseHtmlOptions } from '@change-corgi/core-react/html';
import type { TranslateHtmlReplaceFn } from '@change-corgi/core-react/i18n';

type Options = Pick<LinkProps, 'variant' | 'size' | 'className'> & {
	onClick?: () => void;
};

/**
 * Builds a `replace` function for `TranslateHtml`/`translateHtml`/`parseHtml` that swaps a plain
 * <a> parsed from translated copy for the design-system Link, instead of leaving it as bare,
 * unstyled markup. Forwards whatever attributes survive HTML sanitization (target, rel,
 * data-*, ...) rather than hand-picking a few - i18n copy can carry attributes (e.g.
 * data-pass-thru, used to bypass a cookie-consent-gating script) that matter beyond styling.
 */
export function createAnchorToLinkReplacer({
	variant = 'secondary',
	...options
}: Options = {}): TranslateHtmlReplaceFn {
	return ({ name, attribs, children }, { domToReact }) => {
		if (name !== 'a') return undefined;

		const { href, class: cssClass, style, ...rest } = attribs;

		// A missing href would otherwise fall through to the Link's own "#" default, rendering a
		// dead, clickable link where the source markup had none - render the text plainly instead.
		if (!href) return <>{domToReact(children)}</>;

		return (
			<Link {...rest} href={href} variant={variant} {...options}>
				{domToReact(children)}
			</Link>
		);
	};
}

/**
 * Convenience wrapper for the two call sites (`PageFailure`, `useLoginModal`) that call
 * `parseHtml` directly on an already-translated string rather than going through
 * `TranslateHtml`/`translateHtml` - both need it because they don't have access to a
 * `translateHtml`-shaped helper (a plain `useUtilityContext().i18n` only exposes `translate`,
 * and `useLoginModal` isn't a component, so `<TranslateHtml replace={...} />` isn't an option
 * either). Hides the `{ replace: createAnchorToLinkReplacer(options) }` wiring so callers don't
 * need to know about the replacer at all.
 */
export function parseHtmlWithLink(
	html: string,
	{ variant, size, className, onClick, ...parseHtmlOptions }: Options & Omit<ParseHtmlOptions, 'replace'> = {},
): ReturnType<typeof parseHtml> {
	return parseHtml(html, {
		...parseHtmlOptions,
		replace: createAnchorToLinkReplacer({ variant, size, className, onClick }),
	});
}
